import React from "react";
import { useTranslation } from "react-i18next";
import { LicensePlan, isContactSalesPlan } from "../../../store/actions/PlanActions";
import Button from "../TailwindComponents/Button";
import Badge from "../TailwindComponents/Badge";
import {
  PiUsersLight,
  PiBriefcaseLight,
  PiLightningLight,
  PiHardDriveLight,
  PiShieldCheckLight,
  PiRocketLaunchLight,
  PiCrownLight,
  PiPackageLight,
  PiArrowUpLight,
  PiArrowDownLight,
  PiEnvelopeSimpleLight,
} from "react-icons/pi";
import { IconType } from "react-icons";
import { formatPlanPrice } from "./planCatalogue";

const TIER_ICONS: Record<string, { icon: IconType; color: string }> = {
  starter: { icon: PiShieldCheckLight, color: "text-fg-strong" },
  professional: { icon: PiRocketLaunchLight, color: "text-fg-strong" },
  enterprise: { icon: PiCrownLight, color: "text-fg-strong" },
};

const getTierIcon = (planName: string) => {
  const key = planName.toLowerCase();
  for (const [tier, cfg] of Object.entries(TIER_ICONS)) {
    if (key.includes(tier)) return cfg;
  }
  return { icon: PiPackageLight, color: "text-fg-strong" };
};

interface PlanCardProps {
  plan: LicensePlan;
  billingPeriod: "monthly" | "annual";
  isCurrentPlan: boolean;
  currentPlanSortOrder?: number;
  onSelect: (plan: LicensePlan) => void;
  onViewDetails: (plan: LicensePlan) => void;
  isLoading?: boolean;
  isOrgAdmin?: boolean;
  priceCurrency?: string | null;
  isCurrentPlanBundle?: boolean;
}

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  billingPeriod,
  isCurrentPlan,
  currentPlanSortOrder,
  onSelect,
  onViewDetails,
  isLoading,
  isOrgAdmin = true,
  priceCurrency,
}) => {
  const { t } = useTranslation();
  const price = billingPeriod === "monthly" ? plan.price_monthly : plan.price_annual;
  const gates = plan.feature_gates || { sections: [], ai_features: [], limits: {} };
  const tierCfg = getTierIcon(plan.name || plan.display_name);
  const TierIcon = tierCfg.icon;

  // Sold by the team (Enterprise): no checkout, so no price and no Upgrade.
  const contactSales = isContactSalesPlan(plan);
  const isDowngrade = !isCurrentPlan && currentPlanSortOrder !== undefined && plan.sort_order < currentPlanSortOrder;
  // When the current plan is not one of these (Founding, a custom package),
  // there is no order to compare against - "Upgrade" was a guess, and wrong
  // for a firm on full access. Say "Choose plan" instead.
  const orderUnknown = currentPlanSortOrder === undefined;
  const actionLabel = isCurrentPlan
    ? t("Current plan")
    : !isOrgAdmin
      ? t("Request upgrade")
      : contactSales
        ? t("Contact us")
        : orderUnknown
          ? t("Choose plan")
          : isDowngrade
            ? t("Downgrade")
            : t("Upgrade");
  const actionIcon = isCurrentPlan || !isOrgAdmin
    ? undefined
    : contactSales
      ? <PiEnvelopeSimpleLight />
      : orderUnknown ? undefined : isDowngrade ? <PiArrowDownLight /> : <PiArrowUpLight />;

  return (
    <div
      className={`
        glass rounded-lg overflow-hidden
        cursor-pointer transition-colors
        ${isCurrentPlan
          ? "bg-champagne/10 border-champagne/40 text-fg-strong"
          : ""
        }
      `}
      onClick={() => onViewDetails(plan)}
    >
      <div className="p-5 flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5">
            <TierIcon className={`h-4 w-4 ${tierCfg.color} flex-shrink-0`} />
            <h4 className="font-medium text-fg-strong text-sm">
              {plan.display_name}
            </h4>
            {isCurrentPlan && (
              <Badge color="green" className="text-xs">
                {t("Current")}
              </Badge>
            )}
          </div>
          <Button
            variant={isCurrentPlan ? "neutral" : !isOrgAdmin ? "outline" : isDowngrade ? "outline" : "primary"}
            size="small"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onSelect(plan); }}
            disabled={isCurrentPlan || isLoading}
            isLoading={isLoading}
            icon={actionIcon}
          >
            {actionLabel}
          </Button>
        </div>

        {/* Description */}
        <p className="text-xs text-fg-muted mb-4">
          {plan.description}
        </p>

        {/* Price */}
        <div className="mb-4">
          {contactSales ? (
            <Badge color="slate" className="text-sm font-medium px-3 py-1">
              <span className="text-fg-strong">{t("Custom pricing")}</span>
            </Badge>
          ) : (
            <Badge color="green" className="text-sm font-medium px-3 py-1">
              <span className="text-fg-strong">{formatPlanPrice(price, priceCurrency)}</span>
              <span className="text-xs font-normal">
                /{plan.is_per_seat ? t("seat") + "/" : ""}{t("mo")}
              </span>
            </Badge>
          )}
          {!contactSales && billingPeriod === "annual" && plan.price_monthly > 0 && (
            <>
              <span className="text-xs text-fg-muted ms-2">
                {t("billed annually")}
              </span>
              <span className="text-xs text-success-600 dark:text-success-400 ms-1">
                {t("Save")} {Math.round(((plan.price_monthly - plan.price_annual) / plan.price_monthly) * 100)}%
              </span>
            </>
          )}
        </div>

        {/* Limits */}
        <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
          {gates.limits.max_seats !== undefined && (
            <div className="flex items-center gap-1.5 text-fg-strong">
              <PiUsersLight className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{gates.limits.max_seats === null ? t("Unlimited seats") : `${gates.limits.max_seats} ${t("seats")}`}</span>
            </div>
          )}
          {gates.limits.max_matters !== undefined && (
            <div className="flex items-center gap-1.5 text-fg-strong">
              <PiBriefcaseLight className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{gates.limits.max_matters === null ? t("Unlimited matters") : `${gates.limits.max_matters} ${t("matters")}`}</span>
            </div>
          )}
          {gates.limits.max_ai_queries_monthly !== undefined && (
            <div className="flex items-center gap-1.5 text-fg-strong">
              <PiLightningLight className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{gates.limits.max_ai_queries_monthly === null ? t("Unlimited AI") : `${gates.limits.max_ai_queries_monthly} ${t("AI/mo")}`}</span>
            </div>
          )}
          {gates.limits.max_storage_gb !== undefined && (
            <div className="flex items-center gap-1.5 text-fg-strong">
              <PiHardDriveLight className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{gates.limits.max_storage_gb === null ? t("Unlimited storage") : `${gates.limits.max_storage_gb} GB`}</span>
            </div>
          )}
        </div>

        {/* What the package contains lives in the comparison table below and
            in the details panel this card opens - listing it in every card as
            well made the page three copies of the same list. */}
        <p className="mt-auto text-xs text-fg-muted">{t("Open for everything included")}</p>
      </div>
    </div>
  );
};

export default PlanCard;
